import React from 'react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback: React.ReactNode;
  resetKeys?: Array<string | number>;
}

interface ErrorBoundaryState {
  hasError: boolean;
  prevResetKeys?: Array<string | number>;
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      prevResetKeys: props.resetKeys,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI.
    void error;
    return { hasError: true };
  }

  static getDerivedStateFromProps(
    nextProps: ErrorBoundaryProps,
    prevState: ErrorBoundaryState,
  ): Partial<ErrorBoundaryState> | null {
    const { resetKeys } = nextProps;
    const { prevResetKeys, hasError } = prevState;

    // Reset error boundary when resetKeys change
    if (hasError && resetKeys && prevResetKeys) {
      if (resetKeys.some((resetKey, idx) => prevResetKeys[idx] !== resetKey)) {
        return {
          hasError: false,
          prevResetKeys: resetKeys,
        };
      }
    }

    return {
      prevResetKeys: resetKeys,
    };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, info);
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return this.props.fallback;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
